import { AddressAutocompleteInput, formatAddress } from '../common/AddressAutocompleteInput';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Switch,
  Chip,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Table,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import { TabbableAccordion } from '../common/TabbableAccordion';
import { GBPDescriptionEditor } from './GBPDescriptionEditor';
import {
  CloudArrowUp as PushIcon,
  CloudArrowDown as PullIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
  PencilSimple as EditIcon,
  FloppyDisk as SaveIcon,
  Buildings as BusinessIcon,
  Phone as PhoneIcon,
  MapPin as LocationIcon,
  Clock as ScheduleIcon,
  Tag as CategoryIcon,
  Gear as ServicesIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@phosphor-icons/react';
import {
  useGBPProfileSettings,
  useUpdateGBPProfileSettings,
  useSyncFromGoogle,
  useSyncToGoogle,
  useCreateGBPService,
  useUpdateGBPService,
  useDeleteGBPService
} from '../../hooks/useGBPProfileSettings';
import type {
  GBPService,
  GBPProfileSettingsUpdate,
  GBPServicePriceType
} from '../../types/marketing';

import { DAYS_OF_WEEK, parseGoogleHours, replaceDayHours, hasComplexHours } from '../../lib/googleBusinessHours';
import type { DayOfWeek, DayHours, EditableHours, GoogleHours } from '../../lib/googleBusinessHours';

// Convert Google hours format to editable UI format
// Solid primary header bars so each collapsible section reads as a clear
// container instead of blending into the page background.
const sectionHeaderSx = {
  '& .MuiAccordionSummary-button': {
    bgcolor: 'primary.solidBg',
    color: 'primary.solidColor',
    borderRadius: 'md',
    py: 1.25,
    px: 2,
    // Joy's default hover/expanded styles swap in a dark text color —
    // keep everything white in every interaction state.
    '&:hover': { bgcolor: 'primary.solidHoverBg', color: 'primary.solidColor' },
    '&:active': { bgcolor: 'primary.solidActiveBg', color: 'primary.solidColor' },
    '&:focus-visible, &.Mui-expanded': { color: 'primary.solidColor' },
    '& .MuiTypography-root': { color: 'inherit' },
  },
  '& .MuiAccordionSummary-indicator': { color: 'primary.solidColor' },
} as const;

// Format hour for display (e.g., 8 -> "8:00 AM", 14 -> "2:00 PM")
const formatHourDisplay = (hour: number, minute: number = 0): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

// Generate hour options for dropdown
const generateHourOptions = (): { value: number; label: string }[] => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    options.push({ value: h, label: formatHourDisplay(h, 0) });
  }
  return options;
};

// Generate minute options for dropdown (15-min intervals)
const generateMinuteOptions = (): { value: number; label: string }[] => {
  return Array.from({ length: 60 }, (_, value) => ({ value, label: `:${String(value).padStart(2, '0')}` }));
};

const HOUR_OPTIONS = generateHourOptions();
const MINUTE_OPTIONS = generateMinuteOptions();

function syncErrorMessage(error: unknown): string {
  const responseData = (error as { responseData?: { error?: string } } | null)?.responseData;
  return responseData?.error || (error instanceof Error ? error.message : 'Unable to sync with Google.');
}

// Format day name for display
const formatDayName = (day: DayOfWeek): string => {
  return day.charAt(0) + day.slice(1).toLowerCase();
};

interface GBPProfileSettingsProps {
  profileId: number;
  onDirtyChange?: (dirty: boolean) => void;
}

export const GBPProfileSettings: React.FC<GBPProfileSettingsProps> = ({ profileId, onDirtyChange }) => {
  const { data: profile, isLoading, error } = useGBPProfileSettings(profileId);
  const updateSettings = useUpdateGBPProfileSettings();
  const syncFromGoogle = useSyncFromGoogle();
  const syncToGoogle = useSyncToGoogle();

  // Local form state
  const [formData, setFormData] = useState<GBPProfileSettingsUpdate>({});
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{ message: string; error: boolean } | null>(null);

  useEffect(() => { onDirtyChange?.(hasLocalChanges); }, [hasLocalChanges, onDirtyChange]);

  // Service modals
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<GBPService | null>(null);

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile && !hasLocalChanges) {
      setFormData({
        location_name: profile.location_name,
        address: profile.address,
        phone: profile.phone,
        website: profile.website,
        description: profile.description,
        hours: profile.hours,
        primary_category: profile.primary_category,
        additional_categories: profile.additional_categories,
        opening_date: profile.opening_date,
        primary_phone: profile.primary_phone,
        additional_phones: profile.additional_phones,
        chat_enabled: profile.chat_enabled,
        social_profiles: profile.social_profiles,
        service_areas: profile.service_areas,
        is_service_area_business: profile.is_service_area_business,
        special_hours: profile.special_hours,
        attributes: profile.attributes,
      });
      setHasLocalChanges(false);
    }
  }, [profile, hasLocalChanges]);

  const handleFieldChange = <K extends keyof GBPProfileSettingsUpdate>(
    field: K,
    value: GBPProfileSettingsUpdate[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasLocalChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!profileId) return;
    try {
      await updateSettings.mutateAsync({ profileId, data: formData });
      setHasLocalChanges(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  const handlePullFromGoogle = async () => {
    setSyncNotice(null);
    try {
      const result = await syncFromGoogle.mutateAsync(profileId);
      if (result.warning) setSyncNotice({ message: result.warning, error: false });
    } catch (error) {
      setSyncNotice({ message: syncErrorMessage(error), error: true });
      console.error('Failed to sync from Google:', error);
    }
  };

  const handlePushToGoogle = async () => {
    setSyncNotice(null);
    try {
      await syncToGoogle.mutateAsync(profileId);
    } catch (error) {
      setSyncNotice({ message: syncErrorMessage(error), error: true });
      console.error('Failed to sync to Google:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load profile settings. Please try again.
      </Alert>
    );
  }

  if (!profile) {
    return (
      <Alert color="neutral">
        No profile data available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Sync Status Bar */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography level="title-md">{profile.location_name}</Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                {profile.last_google_sync && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Last synced: {new Date(profile.last_google_sync).toLocaleString()}
                  </Typography>
                )}
                {(profile.has_pending_changes || hasLocalChanges) && (
                  <Chip
                    size="sm"
                    color="warning"
                    variant="soft"
                    startDecorator={<WarningIcon size={18} />}
                  >
                    {hasLocalChanges ? 'Unsaved Changes' : 'Pending Google Sync'}
                  </Chip>
                )}
                {!profile.has_pending_changes && !hasLocalChanges && (
                  <Chip
                    size="sm"
                    color="neutral"
                    variant="soft"
                    startDecorator={<CheckIcon size={18} />}
                  >
                    {profile.connection_status === 'location_required' ? 'Location not linked' : 'No pending changes'}
                  </Chip>
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              {hasLocalChanges && (
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<SaveIcon />}
                  onClick={handleSaveChanges}
                  loading={updateSettings.isPending}
                >
                  Save Changes
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<PullIcon />}
                onClick={handlePullFromGoogle}
                loading={syncFromGoogle.isPending}
                disabled={hasLocalChanges || profile.has_pending_changes || syncToGoogle.isPending}
              >
                Pull from Google
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<PushIcon />}
                onClick={handlePushToGoogle}
                loading={syncToGoogle.isPending}
                disabled={!profile.has_pending_changes || hasLocalChanges || syncFromGoogle.isPending}
              >
                Push to Google
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {syncNotice && (
        <Alert color={syncNotice.error ? 'danger' : 'warning'} sx={{ mb: 2 }}>
          {syncNotice.message}
        </Alert>
      )}
      {(hasLocalChanges || profile.has_pending_changes) && (
        <Typography level="body-sm" sx={{ mb: 2 }}>
          {hasLocalChanges
            ? 'Save your edits, then push to Google before pulling updates.'
            : 'Your edits are saved here. Push to Google before pulling updates.'}
        </Typography>
      )}

      {/* Settings Sections */}
      <AccordionGroup sx={{ maxWidth: '100%', gap: 1, '& > .MuiAccordion-root': { borderBottom: 'none' } }}>
        {/* About Section */}
        <TabbableAccordion defaultExpanded>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              <Typography level="title-md">About</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl>
                <FormLabel>Business Name</FormLabel>
                <Input
                  value={formData.location_name || ''}
                  onChange={(e) => handleFieldChange('location_name', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Primary Category</FormLabel>
                <Input
                  value={formData.primary_category || ''}
                  onChange={(e) => handleFieldChange('primary_category', e.target.value)}
                  placeholder="e.g., Roofing contractor"
                />
              </FormControl>

              <GBPDescriptionEditor
                profileId={profileId} target="business"
                businessName={formData.location_name} primaryCategory={formData.primary_category}
                value={formData.description || ''}
                onChange={value => handleFieldChange('description', value)}
              />

              <FormControl>
                <FormLabel>Opening Date</FormLabel>
                <Input
                  type="date"
                  value={formData.opening_date || ''}
                  onChange={(e) => handleFieldChange('opening_date', e.target.value || null)}
                />
              </FormControl>
            </Stack>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Contact Section */}
        <TabbableAccordion>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon />
              <Typography level="title-md">Contact</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl>
                <FormLabel>Primary Phone</FormLabel>
                <Input
                  value={formData.primary_phone || formData.phone || ''}
                  onChange={(e) => handleFieldChange('primary_phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Website</FormLabel>
                <Input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => handleFieldChange('website', e.target.value)}
                  placeholder="https://www.example.com"
                />
              </FormControl>

              <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                <FormLabel>Chat Enabled</FormLabel>
                <Switch
                  checked={formData.chat_enabled || false}
                  onChange={(e) => handleFieldChange('chat_enabled', e.target.checked)}
                />
              </FormControl>

            </Stack>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Location Section */}
        <TabbableAccordion>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              <Typography level="title-md">Location</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl>
                <FormLabel>Business Address</FormLabel>
                <AddressAutocompleteInput
                  value={formData.address || ''}
                  onChange={(value) => handleFieldChange('address', value)}
                  onAddressSelect={(parsed) => handleFieldChange('address', formatAddress(parsed))}
                />
              </FormControl>

              <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                <Box>
                  <FormLabel>Service Area Business</FormLabel>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Hide address and show service areas instead
                  </Typography>
                </Box>
                <Switch
                  checked={formData.is_service_area_business || false}
                  onChange={(e) => handleFieldChange('is_service_area_business', e.target.checked)}
                />
              </FormControl>

              {formData.is_service_area_business && (
                <FormControl>
                  <FormLabel>Service Areas</FormLabel>
                  <Textarea
                    value={(formData.service_areas || []).join('\n')}
                    onChange={(e) => handleFieldChange(
                      'service_areas',
                      e.target.value.split('\n').filter(Boolean)
                    )}
                    minRows={3}
                    placeholder="Enter each service area on a new line&#10;e.g., Springfield, IL&#10;Chatham, IL"
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    Enter each service area on a new line
                  </Typography>
                </FormControl>
              )}
            </Stack>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Hours Section */}
        <TabbableAccordion>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon />
              <Typography level="title-md">Hours</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ pt: 1 }}>
              <HoursEditor
                hours={formData.hours as GoogleHours | null | undefined}
                onChange={(newHours) => handleFieldChange('hours', newHours)}
              />
            </Box>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Services Section */}
        <TabbableAccordion>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ServicesIcon />
              <Typography level="title-md">Services</Typography>
              <Chip size="sm" variant="soft">{profile.services?.length || 0}</Chip>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  size="sm"
                  startDecorator={<AddIcon />}
                  onClick={() => {
                    setEditingService(null);
                    setShowServiceModal(true);
                  }}
                >
                  Add Service
                </Button>
              </Box>

              {profile.services && profile.services.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Description</th>
                      <th>Price</th>
                      <th style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.services.map((service) => (
                      <tr key={service.id}>
                        <td>
                          <Typography level="body-sm" fontWeight="md">
                            {service.display_name}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {service.description || '-'}
                          </Typography>
                        </td>
                        <td>
                          <ServicePriceDisplay service={service} />
                        </td>
                        <td>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              aria-label={`Edit ${service.display_name}`}
                              size="sm"
                              variant="plain"
                              onClick={() => {
                                setEditingService(service);
                                setShowServiceModal(true);
                              }}
                            >
                              <EditIcon size={18} />
                            </IconButton>
                            <DeleteServiceButton
                              serviceId={service.id}
                              profileId={profileId}
                            />
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert color="neutral" size="sm">
                  No services added yet. Add services to show what your business offers.
                </Alert>
              )}
            </Box>
          </AccordionDetails>
        </TabbableAccordion>


        {/* Attributes Section */}
        <TabbableAccordion>
          <AccordionSummary sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon />
              <Typography level="title-md">Attributes</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert color="neutral" size="sm">
                Business attributes (accessibility, amenities, etc.) are managed through the
                Google Business Profile dashboard. Changes made there will be synced when you pull from Google.
              </Alert>

              {profile.attributes && Object.keys(profile.attributes).length > 0 && (
                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Current Attributes</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {Object.entries(profile.attributes).map(([key, value]) => (
                      <Chip key={key} size="sm" variant="soft">
                        {key}: {String(value)}
                      </Chip>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </TabbableAccordion>
      </AccordionGroup>

      {/* Service Modal */}
      {showServiceModal && <ServiceModal
        key={`${profileId}:${editingService?.id ?? 'new'}`}
        open={showServiceModal}
        onClose={() => {
          setShowServiceModal(false);
          setEditingService(null);
        }}
        service={editingService}
        profileId={profileId}
        businessName={formData.location_name}
        primaryCategory={formData.primary_category}
      />}
    </Box>
  );
};

// Hours Editor Component
interface HoursEditorProps {
  hours: GoogleHours | null | undefined;
  onChange: (hours: GoogleHours) => void;
}

const HoursEditor: React.FC<HoursEditorProps> = ({ hours, onChange }) => {
  const [hoursError, setHoursError] = useState('');
  const [editableHours, setEditableHours] = useState<EditableHours>(() =>
    parseGoogleHours(hours)
  );

  // Update editable hours when profile hours change (e.g., after pull from Google)
  useEffect(() => {
    setEditableHours(parseGoogleHours(hours));
  }, [hours]);

  const handleDayChange = (
    day: DayOfWeek,
    field: keyof DayHours,
    value: boolean | number
  ) => {
    const newHours = {
      ...editableHours,
      [day]: {
        ...editableHours[day],
        [field]: value,
      },
    };
    const changed = newHours[day];
    if (changed.isOpen && changed.closeHour * 60 + changed.closeMinute <= changed.openHour * 60 + changed.openMinute) {
      setHoursError('Closing time must be after opening time. Adjust the closing time first, or manage overnight hours in Google Business Profile. Your previous hours are unchanged.');
      return;
    }
    setHoursError('');
    setEditableHours(newHours);
    onChange(replaceDayHours(hours, day, newHours[day]));
  };

  return (
    <Stack spacing={1}>
      {hoursError && <Alert color="warning">{hoursError}</Alert>}
      {DAYS_OF_WEEK.map((day) => {
        const dayHours = editableHours[day];
        if (hasComplexHours(hours, day)) return <Alert key={day} color="neutral">
          {formatDayName(day)} has split, overnight, or 24-hour hours. These are preserved. Edit this schedule in Google Business Profile, then pull from Google.
        </Alert>;
        return (
          <Box
            key={day}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              py: 1,
              px: 1.5,
              borderRadius: 'sm',
              bgcolor: dayHours.isOpen ? 'background.surface' : 'background.level1',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Day Name */}
            <Typography
              level="body-sm"
              fontWeight="md"
              sx={{ minWidth: 100 }}
            >
              {formatDayName(day)}
            </Typography>

            {/* Open/Closed Toggle */}
            <Switch
              aria-label={`${formatDayName(day)} open`}
              checked={dayHours.isOpen}
              onChange={(e) => handleDayChange(day, 'isOpen', e.target.checked)}
              size="sm"
            />

            {dayHours.isOpen ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: '1 1 300px', flexWrap: 'wrap', gap: 1 }}>
                {/* Open Time */}
                <Select
                  size="sm"
                  aria-label={`${formatDayName(day)} opening hour`}
                  value={dayHours.openHour}
                  onChange={(_, value) => value !== null && handleDayChange(day, 'openHour', value)}
                  sx={{ minWidth: 110 }}
                >
                  {HOUR_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  aria-label={`${formatDayName(day)} opening minute`}
                  value={dayHours.openMinute}
                  onChange={(_, value) => value !== null && handleDayChange(day, 'openMinute', value)}
                  sx={{ minWidth: 70 }}
                >
                  {MINUTE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>

                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  to
                </Typography>

                {/* Close Time */}
                <Select
                  size="sm"
                  aria-label={`${formatDayName(day)} closing hour`}
                  value={dayHours.closeHour}
                  onChange={(_, value) => value !== null && handleDayChange(day, 'closeHour', value)}
                  sx={{ minWidth: 110 }}
                >
                  {HOUR_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  aria-label={`${formatDayName(day)} closing minute`}
                  value={dayHours.closeMinute}
                  onChange={(_, value) => value !== null && handleDayChange(day, 'closeMinute', value)}
                  sx={{ minWidth: 70 }}
                >
                  {MINUTE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Stack>
            ) : (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                Closed
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

// Helper component to display service price
const ServicePriceDisplay: React.FC<{ service: GBPService }> = ({ service }) => {
  switch (service.price_type) {
    case 'free':
      return <Typography level="body-sm">Free</Typography>;
    case 'fixed':
      return (
        <Typography level="body-sm">
          {service.currency_code} {service.price}
        </Typography>
      );
    case 'range':
      return (
        <Typography level="body-sm">
          {service.currency_code} {service.price_min} - {service.price_max}
        </Typography>
      );
    default:
      return <Typography level="body-sm">-</Typography>;
  }
};

// Delete service button component
const DeleteServiceButton: React.FC<{ serviceId: number; profileId: number }> = ({
  serviceId,
  profileId
}) => {
  const deleteService = useDeleteGBPService();

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      deleteService.mutate({ id: serviceId, profileId });
    }
  };

  return (
    <IconButton
      size="sm"
      variant="plain"
      color="danger"
      onClick={handleDelete}
      loading={deleteService.isPending}
    >
      <DeleteIcon size={18} />
    </IconButton>
  );
};

// Service Modal Component
interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  service: GBPService | null;
  profileId: number;
  businessName?: string;
  primaryCategory?: string;
}

const ServiceModal: React.FC<ServiceModalProps> = ({ open, onClose, service, profileId, businessName, primaryCategory }) => {
  const createService = useCreateGBPService();
  const updateService = useUpdateGBPService();
  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    price_type: 'no_price' as GBPServicePriceType,
    price: '',
    price_min: '',
    price_max: '',
    currency_code: 'USD',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    if (service) {
      setFormData({
        display_name: service.display_name,
        description: service.description,
        price_type: service.price_type,
        price: service.price?.toString() || '',
        price_min: service.price_min?.toString() || '',
        price_max: service.price_max?.toString() || '',
        currency_code: service.currency_code,
        is_active: service.is_active,
        order: service.order
      });
    } else {
      setFormData({
        display_name: '',
        description: '',
        price_type: 'no_price',
        price: '',
        price_min: '',
        price_max: '',
        currency_code: 'USD',
        is_active: true,
        order: 0
      });
    }
  }, [service, open]);

  const handleSubmit = async () => {
    const data = {
      google_business_profile: profileId,
      display_name: formData.display_name,
      description: formData.description,
      price_type: formData.price_type,
      price: formData.price ? parseFloat(formData.price) : null,
      price_min: formData.price_min ? parseFloat(formData.price_min) : null,
      price_max: formData.price_max ? parseFloat(formData.price_max) : null,
      currency_code: formData.currency_code,
      is_active: formData.is_active,
      order: formData.order
    };

    try {
      if (service) {
        await updateService.mutateAsync({ id: service.id, data });
      } else {
        await createService.mutateAsync(data);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save service:', error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 520, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}>
        <ModalClose />
        <DialogTitle>{service ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl required>
              <FormLabel>Service Name</FormLabel>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Roof Inspection"
              />
            </FormControl>

            <GBPDescriptionEditor
              profileId={profileId} target="service" serviceName={formData.display_name}
              businessName={businessName} primaryCategory={primaryCategory}
              value={formData.description}
              onChange={value => setFormData(previous => ({ ...previous, description: value }))}
            />

            <FormControl>
              <FormLabel>Price Type</FormLabel>
              <Select
                value={formData.price_type}
                onChange={(_, value) => setFormData({ ...formData, price_type: value as GBPServicePriceType })}
              >
                <Option value="no_price">No Price</Option>
                <Option value="free">Free</Option>
                <Option value="fixed">Fixed Price</Option>
                <Option value="range">Price Range</Option>
              </Select>
            </FormControl>

            {formData.price_type === 'fixed' && (
              <FormControl>
                <FormLabel>Price</FormLabel>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  startDecorator={formData.currency_code}
                />
              </FormControl>
            )}

            {formData.price_type === 'range' && (
              <Stack direction="row" spacing={1}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Min Price</FormLabel>
                  <Input
                    type="number"
                    value={formData.price_min}
                    onChange={(e) => setFormData({ ...formData, price_min: e.target.value })}
                    startDecorator={formData.currency_code}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Max Price</FormLabel>
                  <Input
                    type="number"
                    value={formData.price_max}
                    onChange={(e) => setFormData({ ...formData, price_max: e.target.value })}
                    startDecorator={formData.currency_code}
                  />
                </FormControl>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={createService.isPending || updateService.isPending}
            disabled={!formData.display_name}
          >
            {service ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
